import { useEffect, useState } from "react";
import {
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Modal,
    TextField,
    Chip,
    ToggleButtonGroup,
    ToggleButton
} from "@mui/material";
import { Delete, Edit, Add, Download, GetApp, CloudUpload, HelpOutline, GridView, ViewList, Create } from "@mui/icons-material";
import FolderIcon from "@mui/icons-material/Folder";
import Layout from "../../../components/layouts/Layout";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { FolderWithDocuments, Document, RecentDocument } from "../../../types/folderTypes";
import DragAndDropUploadWithFolder from "../../../components/DragAndDropUploadWithFolder";
import { updateFileSuperAdmin } from "../../../service/fileService";
import ConfirmDialog from "../../../components/dialog/ConfirmationDialog";
import { useSearchParams } from "react-router-dom";
import AdminFolderService from "../../../service/admin/folderService";
import FolderDocumentTable from "../../../components/folderRepo/FolderDocumentTable";
import FolderDocumentGrid from "../../../components/folderRepo/FolderDocumentGrid";
import RecentDocumentsTable from "../../../components/folderRepo/RecentDocumentsTable";

const FolderRepository = () => {
    const navigate = useNavigate();
    const { spaceId: spaceId } = useParams();

    const [folderModalOpen, setFolderModalOpen] = useState<boolean>(false);
    const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
    const [editingFolder, setEditingFolder] = useState<FolderWithDocuments | null>(null);
    const [editingFolderName, setEditingFolderName] = useState<string>("");

    const [editingFile, setEditingFile] = useState<Document | null>(null);
    const [editingFileName, setEditingFileName] = useState<string>("");

    const [folders, setFolders] = useState<FolderWithDocuments[]>([]);
    const [unassignedDocuments, setUnassignedDocuments] = useState<Document[]>([]);
    const [recentDocuments, setRecentDocuments] = useState<RecentDocument[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [deleteFolderComfirmOpen, setDeleteFolderComfirmOpen] = useState(false);
    const [deleteFolderId, setDeleteFolderId] = useState<number | null>(null);

    const [deleteFileComfirmOpen, setDeleteFileComfirmOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<string | null>(null);

    const [zipModalOpen, setZipModalOpen] = useState<boolean>(false);
    const [zipFileName, setZipFileName] = useState<string>("");

    const [createFolderModalOpen, setCreateFolderModalOpen] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string>("");

    const [spaceName, setSpaceName] = useState<string>('');

    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    // const userId = userData.id;
    // const spaceId = passedSpaceId;

    const [folderAnchorEl, setFolderAnchorEl] = useState<null | HTMLElement>(null);
    const [fileAnchorEl, setFileAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedFile, setSelectedFile] = useState<Document | null>(null);
    const [selectedFolder, setSelectedFolder] = useState<FolderWithDocuments | null>(null);

    const [viewMode, setViewMode] = useState<"grid" | "list">("grid"); // 👈 View Mode State

    const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: "grid" | "list" | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    const handleFolderMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, folder: FolderWithDocuments) => {
        event.stopPropagation(); // Prevents triggering folder click event
        setSelectedFolder(folder);
        setFolderAnchorEl(event.currentTarget);
    };

    const handleFolderMenuClose = () => {
        setFolderAnchorEl(null);
        setSelectedFolder(null);
    };

    const handleFileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, file: Document) => {
        event.stopPropagation();
        setSelectedFile(file);
        setFileAnchorEl(event.currentTarget);
    };

    const handleFileMenuClose = () => {
        setFileAnchorEl(null);
        setSelectedFile(null);
    };

    useEffect(() => {
        if (spaceId) {
            fetchFolders(spaceId);
        }
    }, [spaceId]);

    const fetchFolders = async (spaceId: string) => {
        try {
            setLoading(true);
            const data = await AdminFolderService.getSpaceContent(spaceId);

            setSpaceName(data.spaceName);

            if (data && data.folders) {
                const formattedFolders: FolderWithDocuments[] = data.folders.map((folder: FolderWithDocuments) => ({
                    ...folder,
                    documents: folder.documents || [],
                }));

                setFolders(formattedFolders);
            }

            setUnassignedDocuments(data.documents || []);
            setRecentDocuments(data.recentDocuments || []);
        } catch (error) {
            toast.error("Failed to fetch repo folders.");
        } finally {
            setLoading(false);
        }
    };


    const handleFolderClick = (folderId: string) => {
        navigate(`/admin/folder/${folderId}/${spaceId}`);
    };

    const handleEditFolder = (folder: FolderWithDocuments) => {
        setEditingFolder(folder);
        setEditingFolderName(folder.folder_name);
        setFolderModalOpen(true);
    };

    const handleEditDocument = (document: Document) => {
        const nameWithoutExtension = document.document_name.replace(/\.[^/.]+$/, "");
        setEditingFile(document);
        setEditingFileName(nameWithoutExtension);
        setFileModalOpen(true);
    };

    const handleSaveFolderEdit = async () => {
        if (!editingFolder) return;

        try {
            if (spaceId) {
                await AdminFolderService.updateFolder({ space_folder_id: editingFolder.id.toString(), folder_name: editingFolderName, spaceId });
            }
            toast.success("Folder updated successfully!");
            if (spaceId) {
                fetchFolders(spaceId);
            }
            setFolderModalOpen(false);
            setEditingFolder(null);
            setEditingFolderName("");
        } catch (error) {
            toast.error("Failed to update folder.");
        }
    };

    const handleSaveFileEdit = async () => {
        if (!editingFile) return;

        try {
            const fileExtension = editingFile.document_name.split('.').pop();
            const newFileName = `${editingFileName.trim()}.${fileExtension}`; // Keep original extension

            await updateFileSuperAdmin(editingFile.id.toString(), newFileName);
            toast.success("File updated successfully!");
            fetchFolders(spaceId!);
            setFileModalOpen(false);
            setEditingFile(null);
            setEditingFileName("");
        } catch (error) {
            toast.error("Failed to update file.");
        }
    };

    const handleDeleteFolder = (folderId: number) => {
        setDeleteFolderId(folderId);
        setDeleteFolderComfirmOpen(true);
    }

    const handleFolderDelete = async () => {
        if (deleteFolderId && spaceId) {
            try {
                await AdminFolderService.removeFolder(spaceId, deleteFolderId)
                toast.success("Folder deleted successfully!");
                fetchFolders(spaceId);
            } catch (error) {
                toast.error("Failed to delete folder.");
            }
        }
        setDeleteFolderComfirmOpen(false);
    };

    // delete file
    const confirmDeletefile = (fileId: string) => {
        setFileToDelete(fileId);
        setDeleteFileComfirmOpen(true);
    };

    const handleDeleteFile = async () => {
        if (userData.role == 'superadmin') {

        }
        if (!fileToDelete) return;
        try {
            await AdminFolderService.deleteFile(fileToDelete);
            toast.success("File deleted successfully!");
            fetchFolders(spaceId!);
        } catch (error) {
            toast.error("Failed to delete file.");
        } finally {
            setDeleteFileComfirmOpen(false);
            setFileToDelete(null);
        }
    };

    // pass in file name
    const handleDownloadRepo = async () => {
        if (!zipFileName.trim()) {
            toast.error("Please enter a file name.");
            return;
        }
        try {
            await AdminFolderService.downloadRepoZipFolder(spaceId!);
            toast.success("Folder downloaded successfully!");
            setZipModalOpen(false);
            setZipFileName("");
        } catch (error) {
            toast.error("Failed to download folder.");
        }
    };


    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;

        try {
            if (spaceId) {
                await AdminFolderService.addFolder({ folder_name: newFolderName, spaceId });
                toast.success("Folder created successfully!");
                // fetchFolders(userId); // Refresh folder list
                fetchFolders(spaceId);
                setCreateFolderModalOpen(false);
                setNewFolderName("");
            }
        } catch (error) {
            toast.error("Failed to create folder.");
        }
    };

    const handleDownloadZipFolder = async (spaceId: string) => {
        try {
            await AdminFolderService.downloadRepoZipFolder(spaceId);
            toast.success("Zip Folder downloaded successfully!");
        } catch (error) {
            toast.error("Failed to download the file.");
        }
    };

    const handleDownloadFile = async (documentId: string, documentName: string) => {
        try {
            await AdminFolderService.downloadSingleFile(documentId, documentName);
            toast.success("File downloaded successfully!");
        } catch (error) {
            toast.error("Failed to download the file.");
        }
    };

    const handleDownloadFolder = async (folder: any) => {
        try {
            await AdminFolderService.downloadSingleFolder(folder);
            toast.success("Zip Folder downloaded successfully!");
        } catch (error) {
            toast.error("Failed to download the file.");
        }
    };

    const handleDocumentClick = (docPath: string) => {
        window.open(docPath, "_blank");
    };

    const renderActionChip = (actionType: string) => {
        let chipProps = {
            label: "Unknown",
            icon: <HelpOutline className="text-gray-500" />,
            className: "bg-gray-100 text-gray-600",
        };

        switch (actionType) {
            case "create":
                chipProps = {
                    label: "Create",
                    icon: <Create className="text-blue-600" />,
                    className: "bg-blue-100 text-blue-600",
                };
                break;
            case "upload":
                chipProps = {
                    label: "Upload",
                    icon: <CloudUpload className="text-blue-600" />,
                    className: "bg-blue-100 text-blue-600",
                };
                break;
            case "update":
                chipProps = {
                    label: "Modify",
                    icon: <Edit className="text-yellow-600" />,
                    className: "bg-yellow-100 text-yellow-600",
                };
                break;
            case "delete":
                chipProps = {
                    label: "Delete",
                    icon: <Delete className="text-red-600" />,
                    className: "bg-red-100 text-red-600",
                };
                break;
            case "download":
                chipProps = {
                    label: "Download",
                    icon: <GetApp className="text-green-600" />,
                    className: "bg-green-100 text-green-600",
                };
                break;
        }

        return <Chip {...chipProps} size="small" className={`px-2 py-1 rounded-lg font-medium ${chipProps.className}`} />;
    };

    return (
        <Layout>
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-extrabold text-gray-900">Space ({spaceName})</h1>
                <div className="flex gap-2">
                    <button
                        className="bg-[#632DE9] hover:bg-[#4A1FB8] text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                        onClick={() => setCreateFolderModalOpen(true)}
                    >
                        <Add className="text-white" /> Create Folder
                    </button>
                    {
                        (folders.length > 0 || unassignedDocuments.length > 0) && (
                            <button
                                className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                                onClick={() => handleDownloadZipFolder(spaceId!)}
                            >
                                <Download className="text-white" /> Download Folder
                            </button>
                        )
                    }

                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        aria-label="view mode"
                        className="bg-gray-200 rounded-lg"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ViewList color={viewMode === "list" ? "primary" : "inherit"} />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridView color={viewMode === "grid" ? "primary" : "inherit"} />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-6">
                    <CircularProgress />
                </div>
            ) : folders.length === 0 && unassignedDocuments.length === 0 ? (
                <div className="text-center text-gray-500 py-10">
                    <p>No files or folders found.</p>
                    <p className="mt-2">Create a new folder or upload files to get started.</p>
                </div>
            ) : viewMode === "grid" ? (
                <FolderDocumentGrid
                    folders={folders}
                    unassignedDocuments={unassignedDocuments}
                    folderAnchorEl={folderAnchorEl}
                    fileAnchorEl={fileAnchorEl}
                    selectedFolder={selectedFolder}
                    selectedFile={selectedFile}
                    userRole={userData.role}
                    isCompanyAdmin={userData.is_company_admin}
                    handleFolderClick={handleFolderClick}
                    handleFolderMenuOpen={handleFolderMenuOpen}
                    handleFolderMenuClose={handleFolderMenuClose}
                    handleEditFolder={handleEditFolder}
                    handleDownloadFolder={handleDownloadFolder}
                    handleDeleteFolder={handleDeleteFolder}
                    handleDocumentClick={handleDocumentClick}
                    handleFileMenuOpen={handleFileMenuOpen}
                    handleFileMenuClose={handleFileMenuClose}
                    handleEditDocument={handleEditDocument}
                    handleDownloadFile={handleDownloadFile}
                    confirmDeleteFile={confirmDeletefile}
                />
            ) : (
                /* 🟠 List View Mode */
                <FolderDocumentTable
                    folders={folders}
                    unassignedDocuments={unassignedDocuments}
                    folderAnchorEl={folderAnchorEl}
                    fileAnchorEl={fileAnchorEl}
                    selectedFolder={selectedFolder}
                    selectedFile={selectedFile}
                    userRole={userData.role}
                    isCompanyAdmin={userData.is_company_admin}
                    handleFolderClick={handleFolderClick}
                    handleFolderMenuOpen={handleFolderMenuOpen}
                    handleFolderMenuClose={handleFolderMenuClose}
                    handleEditFolder={handleEditFolder}
                    handleDownloadFolder={handleDownloadFolder}
                    handleDeleteFolder={handleDeleteFolder}
                    handleDocumentClick={handleDocumentClick}
                    handleFileMenuOpen={handleFileMenuOpen}
                    handleFileMenuClose={handleFileMenuClose}
                    handleDownloadFile={handleDownloadFile}
                    confirmDeleteFile={confirmDeletefile}
                    handleEditDocument={handleEditDocument}
                />

            )}

            <div className="mt-6">
                <DragAndDropUploadWithFolder spaceId={spaceId!} onUploadSuccess={() => fetchFolders(spaceId!)} />
            </div>

            {recentDocuments.length > 0 && (
                <RecentDocumentsTable
                    recentDocuments={recentDocuments}
                    userRole={userData.role}
                    isCompanyAdmin={userData.is_company_admin}
                    handleDocumentClick={handleDocumentClick}
                    handleDownloadFile={handleDownloadFile}
                    renderActionChip={renderActionChip}
                    handleFolderClick={handleFolderClick} />
            )}

            {/* Create Folder Modal */}
            <Modal open={createFolderModalOpen} onClose={() => setCreateFolderModalOpen(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">Create New Folder</h2>
                    <TextField
                        fullWidth
                        label="Folder Name"
                        variant="outlined"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && newFolderName.trim() && handleCreateFolder()}
                        className="mb-4"
                    />
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setCreateFolderModalOpen(false)} className="mr-2">Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateFolder}
                            disabled={!newFolderName.trim()}
                        >
                            Create
                        </Button>
                    </div>
                </div>
            </Modal>


            {/* Edit Folder Modal */}
            <Modal open={folderModalOpen} onClose={() => setFolderModalOpen(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">Edit Folder</h2>
                    <TextField
                        fullWidth
                        label="Folder Name"
                        variant="outlined"
                        value={editingFolderName}
                        onChange={(e) => setEditingFolderName(e.target.value)}
                        className="mb-4"
                        onKeyDown={(e) => e.key === "Enter" && editingFolderName.trim() && handleSaveFolderEdit()}
                    />
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setFolderModalOpen(false)} className="mr-2">Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveFolderEdit}
                            disabled={!editingFolderName.trim()}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Edit File Modal */}
            <Modal open={fileModalOpen} onClose={() => setFileModalOpen(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">Edit File</h2>
                    <TextField
                        fullWidth
                        label="File Name"
                        variant="outlined"
                        value={editingFileName}
                        onChange={(e) => setEditingFileName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && editingFileName.trim() && handleSaveFileEdit()}
                        className="mb-4"
                    />
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setFileModalOpen(false)} className="mr-2">Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveFileEdit}
                            disabled={!editingFileName.trim()}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>


            <ConfirmDialog
                open={deleteFolderComfirmOpen}
                onClose={() => setDeleteFolderComfirmOpen(false)}
                onConfirm={handleFolderDelete}
                title="Delete Folder"
                message="Are you sure you want to delete this folder? This action cannot be undone."
            />

            <ConfirmDialog
                open={deleteFileComfirmOpen}
                onClose={() => setDeleteFileComfirmOpen(false)}
                onConfirm={handleDeleteFile}
                title="Delete File"
                message="Are you sure you want to delete this file? This action cannot be undone."
            />

            <Modal open={zipModalOpen} onClose={() => setZipModalOpen(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">Enter ZIP File Name</h2>
                    <TextField
                        fullWidth
                        label="File Name"
                        variant="outlined"
                        value={zipFileName}
                        onChange={(e) => setZipFileName(e.target.value)}
                    />
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setZipModalOpen(false)}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={handleDownloadRepo}>
                            Download
                        </Button>
                    </div>
                </div>
            </Modal>

        </Layout>
    );
};

export default FolderRepository;
